/**
 * Reading and creating `profile.json` (contracts/storage-and-permissions.md, data-model.md
 * "Profile document"). The profile is what names a cellar in the owner's selector.
 */
import {
  createProfile,
  migrateProfile,
  withCellarName,
  type Language,
  type Profile,
} from '../domain/profile';
import { ConditionalWriteError, type ObjectStorage } from './fake-storage';

export function profilePath(cellarId: string): string {
  return `cellars/${cellarId}/profile.json`;
}

/**
 * Creates the person's profile at sign-in with "create only if absent": an existing profile
 * (another device, or a name already set) is left exactly as it is.
 */
export async function ensureProfile(
  storage: ObjectStorage,
  cellarId: string,
  email: string,
): Promise<void> {
  try {
    await storage.put(profilePath(cellarId), createProfile(email), { ifNoneMatch: '*' });
  } catch (error) {
    if (!(error instanceof ConditionalWriteError)) {
      throw error;
    }
  }
}

/**
 * Reads a person's profile as the current version (T076b), whatever version is stored. Undefined
 * when there is none yet. Throws for a profile newer than this code, or not a profile at all.
 */
export async function readProfile(
  storage: ObjectStorage,
  cellarId: string,
): Promise<Profile | undefined> {
  const stored = await storage.get<unknown>(profilePath(cellarId));
  return stored ? migrateProfile(stored.value) : undefined;
}

const MAX_CONFLICT_RETRIES = 3;

/**
 * Changes one setting of the person's profile, as the current version. A conditional write on the
 * profile's ETag, so a change made meanwhile on another device (a name, the language, the digest
 * switch) is reloaded and kept; created only if absent when there is none yet. A profile newer than
 * this code is refused by the migration and never overwritten.
 */
async function updateProfile(
  storage: ObjectStorage,
  cellarId: string,
  email: string,
  change: (profile: Profile) => Profile,
): Promise<void> {
  const path = profilePath(cellarId);
  for (let attempt = 0; ; attempt++) {
    const stored = await storage.get<unknown>(path);
    try {
      if (stored) {
        await storage.put(path, change(migrateProfile(stored.value)), { ifMatch: stored.etag });
      } else {
        await storage.put(path, change(createProfile(email)), { ifNoneMatch: '*' });
      }
      return;
    } catch (error) {
      if (!(error instanceof ConditionalWriteError) || attempt >= MAX_CONFLICT_RETRIES) throw error;
    }
  }
}

/** The language picked under "Réglages" (T076d). */
export function saveLanguage(
  storage: ObjectStorage,
  cellarId: string,
  email: string,
  language: Language,
): Promise<void> {
  return updateProfile(storage, cellarId, email, (profile) => ({ ...profile, language }));
}

/** The monthly email on/off switch under "Réglages" (T111, FR-034). */
export function saveDigestEnabled(
  storage: ObjectStorage,
  cellarId: string,
  email: string,
  digestEnabled: boolean,
): Promise<void> {
  return updateProfile(storage, cellarId, email, (profile) => ({ ...profile, digestEnabled }));
}

/**
 * The cellar's name set under Réglages (owner request, 2026-10-08); empty removes it. Throws
 * InvalidCellarNameError for one too long, before anything is written.
 */
export function saveCellarName(
  storage: ObjectStorage,
  cellarId: string,
  email: string,
  name: string,
): Promise<void> {
  withCellarName(createProfile(email), name);
  return updateProfile(storage, cellarId, email, (profile) => withCellarName(profile, name));
}
